import { IUserTranscriptResponse } from 'src/app/interfaces/iuser-transcript-response';
import {
  Component,
  computed,
  effect,
  Input,
  OnDestroy,
  OnInit,
  output,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';
import { QaiCarouselComponent, QaiCarouselItemDirective, QaiLoadingComponent } from '@qai/ui';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { PathwayRoleplayResultsService } from './service/pathway-roleplay-results.service';
import { PathwayRoleplayService } from 'src/app/pages/learner/call/call-pathway-roleplay/services/pathway-roleplay.service';
import { PathwayRoleplayFooterService } from 'src/app/pages/learner/call/call-pathway-roleplay/services/pathway-roleplay-footer.service';
import { ResultMessagingComponent } from './result-messaging/result-messaging.component';
import { ResultMethodologyComponent } from './result-methodology/result-methodology.component';
import { ResultSkillsComponent } from './result-skills/result-skills.component';
import { ResultOverallScoreComponent } from './result-overall-score/result-overall-score.component';
import { ResultCallTranscriptComponent } from './result-call-transcript/result-call-transcript.component';
import { ResultAccuracyComponent } from './result-accuracy/result-accuracy.component';
import { RoleplayPostCallComponent } from '../roleplay-post-call/roleplay-post-call.component';
import { ScoreKind } from 'src/app/utils/enums/result/resultConversational.enum';
import { CallService } from 'src/app/services/learner/call.service';
import { PathwayStepType } from 'src/app/utils/enums/learner/pathway.enum';

type ProcessingState = 'processing' | 'feedback_given' | 'delay' | 'error';

@Component({
  selector: 'app-results-pathway-roleplay',
  standalone: true,
  imports: [
    QaiCarouselComponent,
    QaiCarouselItemDirective,
    QaiLoadingComponent,
    ResultOverallScoreComponent,
    RoleplayPostCallComponent,
    ResultMessagingComponent,
    ResultMethodologyComponent,
    ResultSkillsComponent,
    ResultCallTranscriptComponent,
    ResultAccuracyComponent,
  ],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'results',
    },
  ],
  templateUrl: './results-pathway-roleplay.component.html',
  styleUrls: ['./results-pathway-roleplay.component.scss'],
})
export class ResultsPathwayRoleplayComponent implements OnInit, OnDestroy {
  @ViewChild(QaiCarouselComponent) private carousel?: QaiCarouselComponent;

  @Input() practiceCallUuid: string = '';
  @Input() callType: string = '';
  @Input() endedEarly: boolean = false;
  readonly cardWidthPx = signal(0);
  readonly cardWidthChanged = output<number>();

  resolvedPracticeCallUuid: string = '';
  translationLoaded: boolean = false;

  readonly resolvedCallType = computed(
    () =>
      this.callType ||
      this.resultsService.conversationalResults()?.callType ||
      '',
  );

  readonly currentState = computed((): ProcessingState => {
    if (this.endedEarly) return 'feedback_given';
    if (this.resultsService.resultReady()) return 'feedback_given';
    if (this.resultsService.isTimedOut()) return 'delay';
    if (this.resultsService.hasError()) return 'error';
    return 'processing';
  });

  readonly showResults = signal(false);
  readonly isMobile = signal(false);
  readonly returnToPathway = output<void>();
  private breakpointSub!: Subscription;

  onViewResults(): void {
    const overallScore = this.resultsService
      .scoresArray()
      .find((s) => s.labelKey === 'OVERALL_SCORE');
    const passed = overallScore ? overallScore.kind !== ScoreKind.Fail : true;
    const hasDebrief =
      this.resultsService.conversationalResults()?.hasDebrief ?? false;
    this.roleplayService.isPostCallVisible.set(false);
    this.roleplayService.resolvePostCallFooterState(hasDebrief, passed);
    this.showResults.set(true);

  }

  constructor(
    public readonly resultsService: PathwayRoleplayResultsService,
    private readonly route: ActivatedRoute,
    private readonly translationService: TranslationService,
    private readonly bp: BreakpointObserver,
    private readonly roleplayService: PathwayRoleplayService,
    private readonly callService: CallService,
    private readonly footerService: PathwayRoleplayFooterService,
  ) {
    this.breakpointSub = this.bp
      .observe('(max-width: 768px)')
      .subscribe((state) => {
        this.isMobile.set(state.matches);
      });

    effect(() => {
      if (!this.footerService.isHidden()) {
        requestAnimationFrame(() => this.carousel?.recalculate());
      }
    });

    effect(() => {
      if (!this.resultsService.scoresArray().length) return;

      const results = this.resultsService.conversationalResults();
      if (results?.stepType !== PathwayStepType.CERTIFICATION_CALL) return;
      if (untracked(() => this.footerService.coachLine())) return;

      const overallScore = this.resultsService.scoresArray().find((s) => s.labelKey === 'OVERALL_SCORE');
      const passed = overallScore ? overallScore.kind !== ScoreKind.Fail : true;

      if (passed) {
        const text = 'Congratulations, you passed this certification.';
        const audio$ = this.callService.getAudioStream(this.resolvedPracticeCallUuid, text);
        this.footerService.setCoachLine({ text, audio$ });
        requestAnimationFrame(() => this.carousel?.recalculate());
        return;
      }

      const isViewResults = this.route.snapshot.queryParamMap.get('viewResults') === 'true';

      if (isViewResults) {
        const nextStepReason = this.route.snapshot.queryParamMap.get('outOFAttempts');
        const text = nextStepReason === 'true'
          ? "You've used all your attempts. Please reach out to your manager to discuss next steps."
          : "You didn't pass this certification. Review the material and try again.";
        const audio$ = this.callService.getAudioStream(this.resolvedPracticeCallUuid, text);
        this.footerService.setCoachLine({ text, audio$ });
        requestAnimationFrame(() => this.carousel?.recalculate());
        return;
      }

      const nextStep = this.roleplayService.nextStepResponse();
      if (!nextStep) return;

      const text = nextStep.reason === 'MAX_ATTEMPT_REACHED'
        ? "You've used all your attempts. Please reach out to your manager to discuss next steps."
        : "You didn't pass this certification. Review the material and try again.";

      const audio$ = this.callService.getAudioStream(this.resolvedPracticeCallUuid, text);
      this.footerService.setCoachLine({ text, audio$ });
      requestAnimationFrame(() => this.carousel?.recalculate());
    });
  }

  ngOnInit(): void {
    this.translationService.loadScopedTranslation('results').then(() => {
      this.translationLoaded = true;
    });

    const params = this.route.snapshot.queryParamMap;
    if (params.get('viewResults') === 'true' || params.get('startDebrief') === 'true') {
      this.showResults.set(true);
    }

    this.resolvedPracticeCallUuid =
      this.practiceCallUuid ||
      this.route.snapshot.queryParamMap.get('practiceCallUuid') ||
      '';
    if (this.resolvedPracticeCallUuid && !this.endedEarly) {
      this.resultsService.init(this.resolvedPracticeCallUuid);
    }
  }

  onCardWidthChange(width: number): void {
    this.cardWidthPx.set(width);
    this.cardWidthChanged.emit(width);
  }

  ngOnDestroy(): void {
    this.resultsService.destroy();
    this.breakpointSub.unsubscribe();
  }
}
